import type { InfiniteData, Query, QueryClient, UseMutationOptions } from "@tanstack/react-query";

import type { EditorScope } from "@/api/contracts/spec-editor";
import type { specificationSummary } from "@/api/functions/specifications";
import {
  specificationLinesQueryKey,
  specificationLinesQueryKeyPrefix,
  specificationReviewLinesQueryKey,
  specificationSummaryByVersionQueryKey,
  specificationSummaryQueryKey,
  specificationSummaryQueryKeyPrefix,
  specificationsListQueryKeyPrefix,
  type SpecLinesGroup,
  type SpecLinesLine,
  type SpecLinesLocation,
  type SpecLinesPage,
  type SpecLinesSelection,
} from "@/hooks/specifications";
import { markSpecHistoryStale } from "@/hooks/spec-history-keys";
import { markWhereToSaveStale } from "@/hooks/spec-over-target-keys";
import { ERRORS, type EditorErrorKey } from "@/lib/spec-editor-errors";

// The Editor's one cache updater and its optimism harness (#797; ADR-0014
// decisions 1–5 as amended).
//
// **The guarantee** (decision 1): after any edit, everything on screen is
// corrected from the verb's `EditorMutationResult`, atomically, with no loading
// state and no second round trip. Every Editor verb's hook calls
// `applyEditorResult` from its `onSuccess` — through `editorMutationOptions`,
// which the nine optimistic verbs give a guess and the rest do not.
//
// **Structural, never inferred** (decision 4). The server computed, in the
// write's transaction, each scope's group order, each affected group's
// ordering and how much of it the caller holds, and the corrected cursor. The
// updater only places what it is told: it never counts, never decides
// membership, never loads a line, and carries none of the source's heuristics
// (dedupe, `onlyOnFull`). The one `max` the display keeps lives in
// `groupDisplayCursor`, on the read side (#797 E1).
//
// **A held line stays held** (#910). The updater never drops a line the view
// holds unless the result removes it or moves it to another group: a line
// past its group's loaded lines — one an edit showed there, as the source's
// `addLine` does — stays where it sat though a later ordering lists it past
// the loaded prefix. Nothing ever shows twice for it: every later page sends
// the ids the view holds (`specificationLinesQueryOptions`) and the server
// leaves them out — the source's `loadLines` dedupe, moved to the server
// (ADR-0014 decision 4's retirement of the client-side one stands).
//
// The pattern is `hooks/catalog-items.ts`' and the lifecycle verbs': cancel
// what is about to be overwritten, `setQueryData` from the response, mark
// what cannot be corrected stale with `refetchType: "none"`, never awaited.

type LinesData = InfiniteData<SpecLinesPage | null, number>;
type Summary = NonNullable<Awaited<ReturnType<typeof specificationSummary>>>;

// What every Editor verb answers with, as the client graph sees it: the
// groups, lines and summary are the reads' own row types, so a result row is a
// read row. Each verb's server function returns the internal wire's face
// (`@/server/data/internal-api/spec-editor`, pinned to Python's snapshot), and
// the compiler checks it against these where the verb passes it as
// `mutationFn`.

/** One group's ordering in one scope: the lines that travel in window order,
 *  how many of them from the first the caller holds, the scope's own count. */
export interface EditorGroupOrdering {
  groupId: number;
  lineCount: number;
  orderedLineIds: number[];
  loadedLineCount: number;
}

/** One scope, corrected: where the next page starts, and what changed. */
export interface EditorScopeResult {
  kind: EditorScope["kind"];
  term: string | null;
  cursor: number;
  total: number;
  hasMore: boolean;
  groupOrder: number[] | null;
  groups: EditorGroupOrdering[];
}

/** The version's gating as the last result left it — the Review & Publish
 *  button's facts, and step 1's (a review page carries its own, #950). */
export interface EditorVersionGating {
  completed: boolean;
  publishable: boolean;
  needsAttentionCount: number;
}

export interface EditorMutationResult {
  groups: SpecLinesGroup[] | null;
  lines: SpecLinesLine[] | null;
  version: (EditorVersionGating & { summary: Summary }) | null;
  scope: { base: EditorScopeResult | null; named: EditorScopeResult | null } | null;
  removedLineIds: number[];
  removedGroupIds: number[];
  /** The version's rooms in the lines read's order — the location verbs that
   *  change the list or a line's rooms carry it (#802); null on every other. */
  locations: SpecLinesLocation[] | null;
}

/** A result carrying nothing — the base a verb's guess lays its own rows or
 *  ordering over (`patchLines`), so nothing it does not name is touched. */
export const NO_ROWS: EditorMutationResult = {
  groups: null,
  lines: null,
  version: null,
  scope: null,
  removedLineIds: [],
  removedGroupIds: [],
  locations: null,
};

/** Where a verb acted: the spec, the version, and the caller's view of it. */
export interface EditorCacheTarget {
  specificationId: number;
  versionId: number;
  scope: EditorScope;
}

export const specificationVersionGatingQueryKey = (specificationId: number, versionId: number) =>
  ["specifications", "version-gating", specificationId, versionId] as const;

/**
 * The version's room list for the Editor session (#965 N12) — the source's
 * `specificLocationIds`, read once from load-metadata and changed only by the
 * location verbs' answers (`loaders.py:168-179`, `objectCache.ts:84-90`). The
 * lines loader seeds it from the first lines page and the checklist reads it;
 * no later page and no search writes it. Never fetched.
 */
export const specificationVersionRoomsQueryKey = (specificationId: number, versionId: number) =>
  ["specifications", "version-rooms", specificationId, versionId] as const;

/**
 * Write a verb's result into the cache: the base view's and the named scope's
 * lines pages, the spec summary, the version's gating, the verb's own change
 * to the session's room list (`rooms`); then every other scope of the
 * version — dropped after a `structural` verb, patched after any other.
 */
export async function applyEditorResult(
  queryClient: QueryClient,
  target: EditorCacheTarget,
  result: EditorMutationResult,
  {
    structural = false,
    rooms,
  }: {
    structural?: boolean;
    /** The verb's own change to the session's room list, where held. */
    rooms?: (held: SpecLinesLocation[]) => SpecLinesLocation[];
  } = {},
): Promise<void> {
  const { specificationId, versionId } = target;
  const corrected = correctedViews(target).map(({ key, isBase }) => ({
    key,
    isBase,
    echo: (isBase ? result.scope?.base : result.scope?.named) ?? null,
  }));

  // A read already on the wire would land after the writes below and put the
  // pre-write state back.
  await Promise.all([
    ...corrected.map(({ key }) => queryClient.cancelQueries({ queryKey: key, exact: true })),
    ...(result.version
      ? [queryClient.cancelQueries(summaryFilter(specificationId, versionId))]
      : []),
  ]);

  // A view is kept fresh only when the result ordered it and its pages could
  // take the correction.
  const kept = new Set<string>();
  for (const { key, echo, isBase } of corrected) {
    const data = queryClient.getQueryData<LinesData>(key);
    if (!data || !patchable(data)) continue;
    const patched = patchLines(data, result, echo, isBase);
    queryClient.setQueryData<LinesData>(
      key,
      echo?.kind === "review" ? withGating(patched, result) : patched,
    );
    if (echo) kept.add(JSON.stringify(key));
  }

  // Every write is a new history row: the header's "Last edit" and an open
  // drawer re-read (#1177; ADR-0028 decision 8), a publish's included.
  markSpecHistoryStale(queryClient, specificationId);
  // And "Where to save"'s rows re-read after it, whether or not the count
  // moved: a swap can keep the count and change the rows (#1243 D2).
  void markWhereToSaveStale(queryClient, specificationId);

  if (result.version) {
    const { summary, ...gating } = result.version;
    // The by-spec key is written even when nothing has read it yet: it is
    // the one the header reads. Every other summary key is written where held.
    queryClient.setQueryData(specificationSummaryQueryKey(specificationId), summary);
    queryClient.setQueriesData(summaryFilter(specificationId, versionId), summary);
    queryClient.setQueryData<EditorVersionGating>(
      specificationVersionGatingQueryKey(specificationId, versionId),
      gating,
    );
  }

  // The session's room list, where held, by the verb's own change alone —
  // never the version's list the result carries, which would bring another
  // tab's rooms in (`objectCache.ts:84-90`, Falcon's `arrayUpdates`).
  if (rooms) {
    queryClient.setQueryData<SpecLinesLocation[]>(
      specificationVersionRoomsQueryKey(specificationId, versionId),
      (held) => held && rooms(held),
    );
  }

  // Every other scope of the version — another term — is never recomputed
  // (#965 N6). A structural verb drops it, as the source's `clearSearchCache`
  // drops every other term's ordering (`objectCache.ts:200-217`): coming back
  // to it reads from cursor 0 behind the search skeleton. Any other verb keeps
  // its ordering and lays the returned rows where it holds them, as the
  // source's rows read its object cache: coming back shows them edited, and
  // nothing is read. A view that cannot take a patch, and a corrected view the
  // result did not order, are marked stale without being read. So is the
  // review step, which is no snapshot (#950): a patch without its ordering
  // would keep the lines a finalize took out of it.
  const correctedKeys = new Set(corrected.map(({ key }) => JSON.stringify(key)));
  const others = queryClient.getQueryCache().findAll({
    queryKey: [...specificationLinesQueryKeyPrefix, specificationId, versionId],
    predicate: (query) =>
      !correctedKeys.has(JSON.stringify(query.queryKey)) && !isReviewKey(query.queryKey),
  });
  for (const query of others) {
    const data = query.state.data as LinesData | undefined;
    if (structural) queryClient.removeQueries({ queryKey: query.queryKey, exact: true });
    else if (data && patchable(data)) {
      queryClient.setQueryData<LinesData>(query.queryKey, patchLines(data, result, null, false));
      kept.add(JSON.stringify(query.queryKey));
    }
  }
  void queryClient.invalidateQueries({
    queryKey: [...specificationLinesQueryKeyPrefix, specificationId, versionId],
    predicate: (query) => !kept.has(JSON.stringify(query.queryKey)),
    refetchType: "none",
  });
}

/**
 * Land a result that carries only its version section — a clone's (#804) or a
 * publish's (#949): write the spec's summary keys and the version's gating with
 * `applyEditorResult`, then mark the spec list's reads stale **without
 * refetching**, so the way back to the list reads afresh. Such a verb changes
 * no line, so the base scope corrects nothing there.
 */
export async function applyVersionSection(
  queryClient: QueryClient,
  { specificationId, versionId }: { specificationId: number; versionId: number },
  result: EditorMutationResult,
): Promise<void> {
  await applyEditorResult(
    queryClient,
    { specificationId, versionId, scope: { kind: "base", cursor: 0 } },
    result,
  );
  void queryClient.invalidateQueries({
    queryKey: specificationsListQueryKeyPrefix,
    refetchType: "none",
  });
}

/**
 * The lines views a result corrects: the base view, and the named scope's own
 * key — a search's term, or the review step's statuses (#950).
 */
function correctedViews({ specificationId, versionId, scope }: EditorCacheTarget) {
  const named =
    scope.kind === "search"
      ? specificationLinesQueryKey(specificationId, versionId, scope.term)
      : scope.kind === "review"
        ? specificationReviewLinesQueryKey(specificationId, versionId, scope.statuses)
        : null;
  return [
    { key: specificationLinesQueryKey(specificationId, versionId), isBase: true },
    ...(named ? [{ key: named, isBase: false }] : []),
  ];
}

/** Whether a lines key is the review step's (`specificationReviewLinesQueryKey`). */
function isReviewKey(key: readonly unknown[]): boolean {
  const scope = key[specificationLinesQueryKeyPrefix.length + 2];
  return typeof scope === "object" && scope !== null && "review" in scope;
}

/**
 * A review's pages with the result's gating, when it carries a version
 * section: step 1's "Continue" and its needs-attention gate follow the write
 * (#950). The pages keep their own gating otherwise.
 */
function withGating(data: LinesData, result: EditorMutationResult): LinesData {
  if (!result.version) return data;
  const { completed, publishable, needsAttentionCount } = result.version;
  const gating: EditorVersionGating = { completed, publishable, needsAttentionCount };
  return { ...data, pages: data.pages.map((page) => page && { ...page, gating }) };
}

/**
 * The spec's summary keys: the by-spec key, the acted-on version's by-version
 * key — its first read may still be on the wire, with no data to tell whose it
 * is — and any other by-version key holding this spec.
 */
function summaryFilter(specificationId: number, versionId: number) {
  const bySpec = JSON.stringify(specificationSummaryQueryKey(specificationId));
  const byVersion = JSON.stringify(specificationSummaryByVersionQueryKey(versionId));
  return {
    queryKey: specificationSummaryQueryKeyPrefix,
    predicate: (query: Query) => {
      const key = JSON.stringify(query.queryKey);
      return (
        key === bySpec ||
        key === byVersion ||
        (query.queryKey[specificationSummaryQueryKeyPrefix.length] === "by-version" &&
          (query.state.data as Summary | null | undefined)?.id === specificationId)
      );
    },
  };
}

/** Pages a correction can be laid over: every page loaded. A failed page is
 *  `null`, and nothing says where its rows sat. */
function patchable(data: LinesData): data is InfiniteData<SpecLinesPage, number> {
  return data.pages.length > 0 && data.pages.every((page) => page !== null);
}

/**
 * One lines page set, corrected. With the scope's ordering (`echo`): the
 * group order and each ordered group's loaded lines are the server's, the last
 * page's `cursor`, `total` and `hasMore` the corrected ones. Without one, only
 * the returned models are replaced where they already sit. Either way a held
 * row the result neither holds nor removes stays, after its nearest held
 * neighbour — a line past its group's loaded lines included, so two lines
 * added in turn both stay, in the order they came (#910) — except in the
 * review step, which is no snapshot (#950): there the server's group order and
 * orderings are the whole view, so a line that left the review (finalized)
 * leaves it, and so does a group with none left. `isBase` says the
 * pages are the base view's, whose count a returned group carries. A result's
 * rooms are laid into every page (`mergeRooms`).
 */
export function patchLines(
  data: LinesData,
  result: EditorMutationResult,
  echo: EditorScopeResult | null,
  isBase: boolean,
): LinesData {
  if (!patchable(data)) return data;
  const pages = data.pages;
  const last = pages[pages.length - 1]!;

  const returnedLines = new Map((result.lines ?? []).map((line) => [line.id, line]));
  const returnedGroups = new Map((result.groups ?? []).map((group) => [group.id, group]));
  const removedLines = new Set(result.removedLineIds);
  const removedGroups = new Set(result.removedGroupIds);
  const orderings = new Map((echo?.groups ?? []).map((ordering) => [ordering.groupId, ordering]));
  // What the orderings hold: each one's loaded prefix. A held line an ordering
  // lists past it stays where it was (#842/r4213882932) — the server cannot
  // know the caller holds it, and the next page skips it as held (#910).
  const placed = new Set(
    (echo?.groups ?? []).flatMap((ordering) =>
      ordering.orderedLineIds.slice(0, ordering.loadedLineCount),
    ),
  );
  const cachedGroups = new Map(last.groups.map((group) => [group.id, group]));
  const cachedLines = new Map<number, SpecLinesLine>();
  const heldByGroup = new Map<number, number[]>();
  for (const line of pages.flatMap((page) => page.lines)) {
    cachedLines.set(line.id, line);
    heldByGroup.set(line.groupId, [...(heldByGroup.get(line.groupId) ?? []), line.id]);
  }

  // The server places what the group order and each ordering list. Every
  // other held group and line stays where it was — a search is a snapshot
  // until the user searches again — unless the result removes it, or a
  // returned model puts the line in another group.
  const previousOrder = last.groups.map((group) => group.id);
  const snapshot = echo?.kind !== "review";
  const order = echo?.groupOrder
    ? keepInPlace(
        echo.groupOrder,
        previousOrder,
        (id) => snapshot && !echo.groupOrder!.includes(id) && !removedGroups.has(id),
      )
    : previousOrder.filter((id) => !removedGroups.has(id));
  const groups = order.flatMap((id) => {
    const cached = cachedGroups.get(id);
    const returned = returnedGroups.get(id);
    const model = returned ?? cached;
    if (!model) return [];
    // A returned group is the base view's model, its count the base view's; a
    // search's own count rides its ordering, and without one keeps its count.
    const lineCount =
      orderings.get(id)?.lineCount ?? (isBase ? undefined : cached?.lineCount) ?? model.lineCount;
    return [{ ...model, lineCount }];
  });

  const lines = groups.flatMap((group) => {
    const previous = heldByGroup.get(group.id) ?? [];
    const stays = (id: number) =>
      !removedLines.has(id) &&
      !placed.has(id) &&
      (returnedLines.get(id)?.groupId ?? group.id) === group.id;
    const ordering = orderings.get(group.id);
    const ids = ordering
      ? keepInPlace(
          ordering.orderedLineIds.slice(0, ordering.loadedLineCount),
          previous,
          (id) => snapshot && stays(id),
        )
      : previous.filter(stays);
    return ids.flatMap((id) => {
      const line = returnedLines.get(id) ?? cachedLines.get(id);
      return line ? [line] : [];
    });
  });

  // Each page keeps its size but the last, which takes what is left: the body
  // reads the pages in order, so only the sequence matters.
  let taken = 0;
  const patched = pages.map((page, index) => {
    const isLast = index === pages.length - 1;
    const chunk = isLast ? lines.slice(taken) : lines.slice(taken, taken + page.lines.length);
    taken += chunk.length;
    return {
      ...page,
      groups,
      lines: chunk,
      ...(result.locations ? { locations: mergeRooms(page.locations, result.locations) } : {}),
      ...(echo ? { total: echo.total } : {}),
      ...(echo && isLast ? { cursor: echo.cursor, hasMore: echo.hasMore } : {}),
    };
  });
  return { ...data, pages: patched };
}

/**
 * One selection of a held line, changed in place by `change` and laid on
 * through `patchLines` — the selection verbs' guesses (#801). A page set that
 * does not hold the line, or a line without the selection, is left as it is.
 * `change` must set values, never toggle them, so the guess stays idempotent.
 */
export function patchSelection(
  data: LinesData,
  lineId: number,
  selectionId: number,
  change: (selection: SpecLinesSelection) => SpecLinesSelection,
): LinesData {
  const line = data.pages.flatMap((page) => page?.lines ?? []).find(({ id }) => id === lineId);
  if (!line?.selections.some(({ id }) => id === selectionId)) return data;
  const selections = line.selections.map((selection) =>
    selection.id === selectionId ? change(selection) : selection,
  );
  return patchLines(data, { ...NO_ROWS, lines: [{ ...line, selections }] }, null, false);
}

/**
 * One held line, changed in place by `change` and laid on through
 * `patchLines` — the location-type guess (#802). A page set that does not hold
 * the line is left as it is. `change` must set values, never toggle them.
 */
export function patchLine(
  data: LinesData,
  lineId: number,
  change: (line: SpecLinesLine) => SpecLinesLine,
): LinesData {
  const line = data.pages.flatMap((page) => page?.lines ?? []).find(({ id }) => id === lineId);
  if (!line) return data;
  return patchLines(data, { ...NO_ROWS, lines: [change(line)] }, null, false);
}

/**
 * A room renamed in place — in every page's room list and on every held
 * placement of it — the rename's guess (#802). A page set that holds neither
 * the room nor a placement of it is left as it is. The name is set, never
 * derived, so the guess stays idempotent.
 */
export function patchRoom(data: LinesData, locationId: number, name: string): LinesData {
  if (!patchable(data)) return data;
  const placed = data.pages
    .flatMap((page) => page.lines)
    .filter((line) => line.locations.some((placement) => placement.locationId === locationId));
  const listed = data.pages.some((page) => page.locations.some(({ id }) => id === locationId));
  if (!listed && placed.length === 0) return data;
  const renamed = placed.map((line) => ({
    ...line,
    locations: line.locations.map((placement) =>
      placement.locationId === locationId ? { ...placement, name } : placement,
    ),
  }));
  const patched = patchLines(data, { ...NO_ROWS, lines: renamed }, null, false);
  return {
    ...patched,
    pages: patched.pages.map((page) =>
      page
        ? {
            ...page,
            locations: page.locations.map((room) =>
              room.id === locationId ? { ...room, name } : room,
            ),
          }
        : page,
    ),
  };
}

/**
 * A manufacturer renamed in place on every held row that shows it — each
 * per-plan selection's own manufacturer and each item's maker with that id —
 * the per-plan dialog's inline rename (#858). The source wrote the renamed row
 * into the shared manufacturer cache every row reads (`LinePerPlan/
 * actions.ts:124-145`), so the rows renamed at once; Atlas's rows carry the
 * name on each pointer, so the same reach is a patch, as `patchRoom`'s is. A
 * page set holding no pointer to it is left as it is. The name is set, never
 * derived, so the patch stays idempotent.
 */
export function patchManufacturer(
  data: LinesData,
  manufacturerId: number,
  name: string,
): LinesData {
  if (!patchable(data)) return data;
  const named = (pointer: { id: number; name: string }) =>
    pointer.id === manufacturerId ? { ...pointer, name } : pointer;
  const shows = (selection: SpecLinesSelection) =>
    selection.manufacturer?.id === manufacturerId ||
    selection.item?.manufacturer.id === manufacturerId;
  const renamed = data.pages
    .flatMap((page) => page.lines)
    .filter((line) => line.selections.some(shows))
    .map((line) => ({
      ...line,
      selections: line.selections.map((selection) =>
        shows(selection)
          ? {
              ...selection,
              manufacturer: selection.manufacturer && named(selection.manufacturer),
              item: selection.item && {
                ...selection.item,
                manufacturer: named(selection.item.manufacturer),
              },
            }
          : selection,
      ),
    }));
  if (renamed.length === 0) return data;
  return patchLines(data, { ...NO_ROWS, lines: renamed }, null, false);
}

/**
 * A page's rooms after a result that carries the version's: the source's
 * session order (`objectCache.ts:87`, #802's parity enumeration S1). A room
 * the page holds keeps its place under the result's name, a room the result
 * adds is appended in the result's order, and one it no longer lists leaves.
 * The checklist reads the session's own list instead (#965 N12), which only
 * the verb's own room changes.
 */
function mergeRooms(
  held: readonly SpecLinesLocation[],
  returned: readonly SpecLinesLocation[],
): SpecLinesLocation[] {
  const returnedById = byId(returned);
  const kept = held.flatMap((room) => {
    const current = returnedById.get(room.id);
    return current ? [current] : [];
  });
  const heldIds = new Set(held.map((room) => room.id));
  return [...kept, ...returned.filter((room) => !heldIds.has(room.id))];
}

/**
 * `placed`, with every previous id `kept` says stays put back after its
 * nearest previous neighbour that is still there — first when none is.
 */
function keepInPlace(
  placed: readonly number[],
  previous: readonly number[],
  kept: (id: number) => boolean,
): number[] {
  const final = [...placed];
  previous.forEach((id, index) => {
    if (!kept(id)) return;
    const before = previous
      .slice(0, index)
      .reverse()
      .find((other) => final.includes(other));
    final.splice(before === undefined ? 0 : final.indexOf(before) + 1, 0, id);
  });
  return final;
}

/**
 * `current` with what a guess changed put back (#965 N8): `guessed` is the
 * guess laid over `before`, and every field, group order and group's line
 * order where the two differ returns to `before`'s, laid over the views as
 * they are now — each source `catch` restoring its own snapshot at once
 * (`SpecEditIsland/data/actions.ts:267-284`). Whatever else moved since — a
 * pending edit's guess, a result that landed, a page loaded — stays. A row
 * gone since is not brought back. A room the guess renamed takes its name
 * back in every page's list.
 */
export function revertGuess(current: LinesData, before: LinesData, guessed: LinesData): LinesData {
  if (!patchable(current) || !patchable(before) || !patchable(guessed)) return current;
  const last = (data: InfiniteData<SpecLinesPage, number>) => data.pages[data.pages.length - 1]!;
  const allLines = (data: InfiniteData<SpecLinesPage, number>) =>
    data.pages.flatMap((page) => page.lines);
  const putBack = <T extends { id: number }>(was: T[], guess: T[], now: T[]): T[] => {
    const wasById = byId(was);
    const nowById = byId(now);
    return guess.flatMap((row) => {
      const before = wasById.get(row.id);
      const present = nowById.get(row.id);
      if (!before || !present) return [];
      const back = revertFields(present, before, row);
      return back === present ? [] : [back];
    });
  };

  const lines = putBack(allLines(before), allLines(guessed), allLines(current));
  const groups = putBack(last(before).groups, last(guessed).groups, last(current).groups);

  const groupIds = (data: InfiniteData<SpecLinesPage, number>) =>
    last(data).groups.map((group) => group.id);
  const lineIds = (data: InfiniteData<SpecLinesPage, number>, groupId: number) =>
    allLines(data)
      .filter((line) => line.groupId === groupId)
      .map((line) => line.id);
  // An order goes back only while the views still hold the guess's: a later
  // drag's stays. A page loaded since adds ids, so only the guess's are read.
  const stillGuessed = (now: number[], guess: number[]) => {
    const ids = new Set(guess);
    return sameIds(
      now.filter((id) => ids.has(id)),
      guess,
    );
  };
  const reordered = (was: number[], guess: number[], now: number[]) =>
    !sameIds(was, guess) && stillGuessed(now, guess);
  const nowGroups = byId(last(current).groups);
  const groupOrder = reordered(groupIds(before), groupIds(guessed), groupIds(current))
    ? groupIds(before)
    : null;
  const orderings = groupIds(before).flatMap((groupId) => {
    const was = lineIds(before, groupId);
    const nowGroup = nowGroups.get(groupId);
    if (!nowGroup || !reordered(was, lineIds(guessed, groupId), lineIds(current, groupId))) {
      return [];
    }
    return [
      {
        groupId,
        lineCount: nowGroup.lineCount,
        orderedLineIds: was,
        loadedLineCount: was.length,
      },
    ];
  });
  const end = last(current);
  const echo: EditorScopeResult | null =
    groupOrder || orderings.length > 0
      ? {
          kind: "base",
          term: null,
          cursor: end.cursor,
          total: end.total,
          hasMore: end.hasMore,
          groupOrder,
          groups: orderings,
        }
      : null;

  const reverted =
    lines.length > 0 || groups.length > 0 || echo
      ? patchLines(current, { ...NO_ROWS, lines, groups }, echo, false)
      : current;
  const roomsWere = last(before).locations;
  const roomsGuessed = last(guessed).locations;
  if (sameValue(roomsWere, roomsGuessed)) return reverted;
  return {
    ...reverted,
    pages: reverted.pages.map((page) =>
      page ? { ...page, locations: revertRows(page.locations, roomsWere, roomsGuessed) } : page,
    ),
  };
}

/** Each of `now`'s rows with what `guess` changed from `was` put back
 *  (`revertFields`); a row either lacks is left as it is. */
function revertRows<T extends { id: number }>(
  now: readonly T[],
  was: readonly T[],
  guess: readonly T[],
): T[] {
  const wasById = byId(was);
  const guessById = byId(guess);
  return now.map((row) => {
    const before = wasById.get(row.id);
    const guessed = guessById.get(row.id);
    return before && guessed ? revertFields(row, before, guessed) : row;
  });
}

/**
 * `now` with each field `guess` changed from `was` put back to `was`'s — a
 * list of rows with ids (a line's selections) row by row, so another
 * selection's change stays. A field `now` no longer holds the guess in keeps
 * what it holds: a later edit's guess to it stays. `now` itself when nothing
 * goes back.
 */
function revertFields<T extends object>(now: T, was: T, guess: T): T {
  let out: T | null = null;
  for (const field of Object.keys(guess) as (keyof T)[]) {
    if (sameValue(guess[field], was[field])) continue;
    const wasValue = was[field];
    const guessValue = guess[field];
    const nowValue = now[field];
    let back: T[keyof T];
    if (isRowList(wasValue) && isRowList(guessValue) && isRowList(nowValue)) {
      back = revertRows(nowValue, wasValue, guessValue) as T[keyof T];
    } else if (sameValue(nowValue, guessValue)) {
      back = wasValue;
    } else {
      continue;
    }
    out = { ...(out ?? now), [field]: back };
  }
  return out ?? now;
}

function isRowList(value: unknown): value is { id: number }[] {
  return (
    Array.isArray(value) &&
    value.every((row) => typeof row === "object" && row !== null && "id" in row)
  );
}

/** Whether two cached values read the same, field by field — whatever order
 *  the fields were spread in. */
function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  return (
    aKeys.length === bKeys.length &&
    aKeys.every((key) =>
      sameValue((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]),
    )
  );
}

/** Whether two id orders are the same order. */
function sameIds(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

function byId<T extends { id: number }>(rows: readonly T[]): Map<number, T> {
  return new Map(rows.map((row) => [row.id, row]));
}

// ---------------------------------------------------------------------------
// The optimism harness
// ---------------------------------------------------------------------------

/** What every Editor verb's mutation is called with, beside its own input. */
export type EditorMutationVariables = EditorCacheTarget;

/** A verb's guess: one cached lines page set → the guessed one. It must be
 *  idempotent — laid over data that already shows it, it changes nothing —
 *  because a pending edit's guess is laid again over another edit's result. */
export type EditorGuess<V> = (data: LinesData, variables: V) => LinesData;

/** A line as the corrected views hold it, by id — `undefined` when none does. */
export type HeldLine = (lineId: number) => SpecLinesLine | undefined;

/** One Editor edit in flight, whether another on its version overlapped it
 *  — then its views are read again once the version's edits settle — and,
 *  for a guessing edit, how to lay its guess over the views again. */
interface EditorFlight {
  overlapped: boolean;
  reguess?: () => void;
}

/** What an edit's `onMutate` hands its later callbacks: its flight, and each
 *  lines entry an optimistic verb overwrote, as it was and as its guess first
 *  made it — what a failure puts back (`revertGuess`). */
export interface EditorSnapshot {
  flight: EditorFlight;
  views: { key: readonly unknown[]; before: LinesData | undefined; guessed?: LinesData }[];
  /** The session's room list, likewise, for a verb that guesses into it. */
  rooms?: { before: SpecLinesLocation[]; guessed: SpecLinesLocation[] };
}

/** A version's edits in flight, and the views a failed overlapping edit
 *  left holding guesses only the server can settle. */
interface VersionFlights {
  flights: Set<EditorFlight>;
  unsettled: Map<string, readonly unknown[]>;
}

const versions = new WeakMap<QueryClient, Map<string, VersionFlights>>();

function versionFlights(
  queryClient: QueryClient,
  { specificationId, versionId }: EditorCacheTarget,
): VersionFlights {
  const byVersion = versions.get(queryClient) ?? new Map<string, VersionFlights>();
  versions.set(queryClient, byVersion);
  const key = JSON.stringify([specificationId, versionId]);
  const entry = byVersion.get(key) ?? { flights: new Set(), unsettled: new Map() };
  byVersion.set(key, entry);
  return entry;
}

const roomsKeyOf = ({ specificationId, versionId }: EditorCacheTarget) =>
  specificationVersionRoomsQueryKey(specificationId, versionId);

/** What an overlapping edit reads again once the version's edits settle:
 *  everything a result writes. */
function overlapRereads(target: EditorCacheTarget): (readonly unknown[])[] {
  const { specificationId, versionId } = target;
  return [
    ...correctedViews(target).map(({ key }) => key),
    specificationSummaryQueryKey(specificationId),
    specificationSummaryByVersionQueryKey(versionId),
    specificationVersionGatingQueryKey(specificationId, versionId),
  ];
}

/**
 * A verb's mutation options. Its failure surfaces as the verb's own `ERRORS`
 * string on the mutation's error, for the caller's error snackbar; a verb with
 * no `error` key (the header write, whose dialog decides what a failure shows)
 * rejects with the server's error as it came. With a
 * `guess` — only the nine verbs ADR-0014 decision 5 (amended) keeps optimistic;
 * the harness does not decide which — the views the result corrects are
 * snapshot, the guess is applied to each, and a failure puts back exactly what
 * the guess changed (`revertGuess`). On success the result overwrites the
 * guess (decision 6.3), and the mutation resolves to the verb's whole answer —
 * Add specification line's carries the new line's id beside the shared result
 * (#800). Every other view of the version is dropped or patched by the result
 * (`structural`), never guessed in: nothing would come back to overwrite a
 * guess there.
 *
 * **Overlapping edits.** A failed edit rolls its own guess back at once,
 * whether or not another edit on the version is in flight (#965 N8; each
 * source `catch` does, `SpecEditIsland/data/actions.ts:267-284`). The
 * rollback is the guess's own change, never the whole snapshot, which may hold
 * another edit's guess, or miss one, or predate a result already written. Two
 * results can answer out of order, the older landing last, with nothing in
 * either to say which is newer. So an overlapped edit's views, the spec's
 * summary and the version's gating are read again once every edit on the
 * version has settled — not sooner, or the server's state would wipe a
 * still-pending edit's guess before that edit lands (S4). For the same reason
 * a result that lands while other edits are pending has their guesses laid
 * over it again: it carries their rows as the server had them before they
 * wrote.
 */
export function editorMutationOptions<
  V extends EditorMutationVariables,
  R extends EditorMutationResult = EditorMutationResult,
>(
  queryClient: QueryClient,
  options: {
    mutationFn: (variables: V) => Promise<R>;
    error?: EditorErrorKey;
    guess?: EditorGuess<V>;
    /** Laid over the restored views when the edit fails, for a verb whose
     *  failure is not the views as they were. */
    failed?: EditorGuess<V>;
    /** A guess into the session's room list (`specificationVersionRoomsQueryKey`),
     *  where held — set, never toggled, so it stays idempotent; rolled back
     *  with the views'. */
    guessRooms?: (rooms: SpecLinesLocation[], variables: V) => SpecLinesLocation[];
    /** The verb's own change to the session's room list once it answers
     *  (#965 N12) — the source's `arrayUpdates` and its one room's name. */
    settleRooms?: (rooms: SpecLinesLocation[], result: R, variables: V) => SpecLinesLocation[];
    /**
     * Whether the result drops every other term the view held (#965 N6) —
     * the verbs whose Falcon answer carries `versionOrderings`, the source's
     * `clearSearchCache`. A function decides per answer, reading the line as
     * the corrected views held it before the result landed.
     */
    structural?: boolean | ((variables: V, result: R, held: HeldLine) => boolean);
  },
): UseMutationOptions<R, Error, V, EditorSnapshot> {
  const { mutationFn, error, guess, failed, guessRooms, settleRooms, structural = false } = options;
  const failure = (cause: unknown) =>
    error === undefined ? cause : new Error(ERRORS[error], { cause });
  // Overlapped or not (#965 N8): what the guess changed goes back at once.
  const restore = (snapshot: EditorSnapshot, variables: V) => {
    for (const { key, before, guessed } of snapshot.views) {
      const current = queryClient.getQueryData<LinesData>(key);
      if (current && before && guessed) {
        queryClient.setQueryData<LinesData>(key, revertGuess(current, before, guessed));
      }
    }
    if (snapshot.rooms) {
      const { before, guessed } = snapshot.rooms;
      queryClient.setQueryData<SpecLinesLocation[]>(
        roomsKeyOf(variables),
        (current) => current && revertRows(current, before, guessed),
      );
    }
  };
  const release = (variables: V, { flight }: EditorSnapshot) => {
    const { flights, unsettled } = versionFlights(queryClient, variables);
    flights.delete(flight);
    if (flight.overlapped) {
      for (const key of overlapRereads(variables)) unsettled.set(JSON.stringify(key), key);
    }
    if (flights.size > 0) return;
    for (const queryKey of unsettled.values()) {
      void queryClient.invalidateQueries({ queryKey, exact: true });
    }
    unsettled.clear();
  };
  return {
    mutationFn: async (variables) => {
      try {
        return await mutationFn(variables);
      } catch (cause) {
        throw failure(cause);
      }
    },
    onMutate: async (variables) => {
      const flight: EditorFlight = { overlapped: false };
      const { flights } = versionFlights(queryClient, variables);
      flights.add(flight);
      if (flights.size > 1) for (const other of flights) other.overlapped = true;
      if (!guess) return { flight, views: [] };
      // TanStack sends nothing after a throwing `onMutate` and hands the later
      // callbacks no snapshot, so a throw here — a bug in a guess — fails the
      // edit as a refused request would, or its flight stays for good (#903).
      const snapshot: EditorSnapshot = { flight, views: [] };
      try {
        const keys = correctedViews(variables).map(({ key }) => key);
        await Promise.all(
          keys.map((queryKey) => queryClient.cancelQueries({ queryKey, exact: true })),
        );
        snapshot.views = keys.map((key) => ({
          key,
          before: queryClient.getQueryData<LinesData>(key),
        }));
        for (const view of snapshot.views) {
          if (!view.before) continue;
          view.guessed = guess(view.before, variables);
          queryClient.setQueryData<LinesData>(view.key, view.guessed);
        }
        const heldRooms = guessRooms
          ? queryClient.getQueryData<SpecLinesLocation[]>(roomsKeyOf(variables))
          : undefined;
        if (guessRooms && heldRooms) {
          snapshot.rooms = { before: heldRooms, guessed: guessRooms(heldRooms, variables) };
          queryClient.setQueryData(roomsKeyOf(variables), snapshot.rooms.guessed);
        }
        flight.reguess = () => {
          for (const key of keys) {
            const data = queryClient.getQueryData<LinesData>(key);
            if (data) queryClient.setQueryData<LinesData>(key, guess(data, variables));
          }
          if (guessRooms) {
            queryClient.setQueryData<SpecLinesLocation[]>(
              roomsKeyOf(variables),
              (rooms) => rooms && guessRooms(rooms, variables),
            );
          }
        };
        return snapshot;
      } catch (cause) {
        restore(snapshot, variables);
        release(variables, snapshot);
        throw failure(cause);
      }
    },
    onError: (_error, variables, snapshot) => {
      if (!snapshot) return;
      restore(snapshot, variables);
      if (!failed) return;
      for (const { key } of snapshot.views) {
        const data = queryClient.getQueryData<LinesData>(key);
        if (data) queryClient.setQueryData<LinesData>(key, failed(data, variables));
      }
    },
    onSuccess: async (result, variables, snapshot) => {
      const held: HeldLine = (lineId) =>
        correctedViews(variables)
          .flatMap(({ key }) => queryClient.getQueryData<LinesData>(key)?.pages ?? [])
          .flatMap((page) => page?.lines ?? [])
          .find(({ id }) => id === lineId);
      await applyEditorResult(queryClient, variables, result, {
        structural:
          typeof structural === "function" ? structural(variables, result, held) : structural,
        ...(settleRooms ? { rooms: (rooms) => settleRooms(rooms, result, variables) } : {}),
      });
      for (const other of versionFlights(queryClient, variables).flights) {
        if (other !== snapshot?.flight) other.reguess?.();
      }
    },
    onSettled: (_result, _error, variables, snapshot) => {
      if (snapshot) release(variables, snapshot);
    },
  };
}
